<script setup>
import { computed, ref } from 'vue'

import { infoBoxToneEnum } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'

const props = defineProps({
  id: {
    type: Number,
    default: null,
  },
  message: {
    type: String,
    default: null,
  },
  tone: {
    type: Number,
    default: infoBoxToneEnum.INFO,
  },
  icon: {
    type: String,
    default: null,
  },
  popup: {
    type: Boolean,
    default: false,
  },
})

const webSiteStore = useWebSiteStore()

const TONE_STYLES = Object.freeze({
  [infoBoxToneEnum.INFO]: { icon: 'mdi-information-outline', color: 'info', background: 'bg-blue-1' },
  [infoBoxToneEnum.SUCCESS]: { icon: 'mdi-check-circle-outline', color: 'positive', background: 'bg-green-1' },
  [infoBoxToneEnum.WARNING]: { icon: 'mdi-alert-circle-outline', color: 'warning', background: 'bg-amber-1' },
  [infoBoxToneEnum.QUESTION]: { icon: 'mdi-help-circle-outline', color: 'primary', background: 'bg-indigo-1' },
})

const toneStyle = computed(() => TONE_STYLES[props.tone] ?? TONE_STYLES[infoBoxToneEnum.INFO])
const isCollapsible = computed(() => props.id !== null)
const isCollapsed = computed(() => isCollapsible.value && webSiteStore.isInfoBoxCollapsed(props.id))

const isIconOnly = ref(isCollapsed.value)

const toggle = () => {
  if (!isCollapsible.value) return
  isIconOnly.value = false
  webSiteStore.toggleInfoBox(props.id)
}

const onTransitionEnd = (event) => {
  if (event.target === event.currentTarget && isCollapsed.value) isIconOnly.value = true
}
</script>

<template>
  <q-icon
    v-if="popup"
    :name="icon || toneStyle.icon"
    :color="toneStyle.color"
    size="sm"
    class="cursor-pointer"
  >
    <q-popup-proxy>
      <q-banner
        :class="['info-box info-box-popup text-grey-9 rounded-borders', toneStyle.background]"
        :style="{ borderColor: `var(--q-${toneStyle.color})` }"
        dense
      >
        <slot>{{ message }}</slot>
      </q-banner>
    </q-popup-proxy>
  </q-icon>
  <div v-else :class="['info-box-wrapper', { 'info-box-collapsed': isCollapsed }]" @transitionend="onTransitionEnd">
    <q-banner
      :class="['info-box text-grey-9 rounded-borders', isIconOnly ? 'info-box-icon-only' : toneStyle.background]"
      :style="{ borderColor: isIconOnly ? 'transparent' : `var(--q-${toneStyle.color})` }"
      dense
      :inline-actions="!!$slots.action"
    >
      <template #avatar>
        <q-icon
          :name="icon || toneStyle.icon"
          :color="toneStyle.color"
          :class="{ 'cursor-pointer': isCollapsible }"
          @click="toggle"
        />
      </template>
      <div class="info-box-body">
        <div class="info-box-body-content">
          <slot>{{ message }}</slot>
        </div>
      </div>
      <template v-if="$slots.action" #action>
        <slot name="action" />
      </template>
    </q-banner>
  </div>
</template>

<style scoped>
.info-box-wrapper {
  display: grid;
  grid-template-columns: 1fr;
  transition: grid-template-columns .2s ease;
}

.info-box {
  border: 1px solid;
  min-width: 2.75rem;
  overflow: hidden;
}

.info-box-popup {
  max-width: 22.5rem;
}

.info-box-icon-only {
  background: transparent;
}

.info-box-body {
  display: grid;
  grid-template-rows: 1fr;
  transition: grid-template-rows .2s ease .2s;
}

.info-box-body-content {
  min-height: 0;
  overflow: hidden;
}

.info-box-collapsed {
  grid-template-columns: 0fr;
  transition-delay: .2s;
}

.info-box-collapsed .info-box-body {
  grid-template-rows: 0fr;
  transition-delay: 0s;
}
</style>
